import { replaceState } from "$app/navigation";
import { page } from "$app/state";
import { useDataSources } from "$lib/core/map/cesium-datasources";
import { selectedMapEntity } from "$lib/core/map/cesium-entity-store";
import { useCesiumViewer } from "$lib/core/map/cesium-viewer";
import { mapModeStore } from "$lib/core/map/map-mode-store";
import { removeAllDrawnPolylineDragHandlesForEditablePolyline, removeAllPointsFromPolyline, removeCornerPointsFromPolyline, removeSelectablePointsFromPolygonEdges, toggleEditPolylineInMap, toggleMovePolygonEdge } from "$lib/core/map/map-tools";
import { isAnyToolEnabled, mapToolsStore } from "$lib/core/map/map-tools-store";
import { cartesianToLatitudeLongitude, entityIsPartOfMastpoint, EntityTypes, getEntityPropertyBag, getMapEntitiesWithinBoundaries, setSelectedEntity, toggleEntityVisuals, toggleHightlightPolygon } from "$lib/core/map/map-utils";
import type { IDrawnPolygonShape } from "$lib/models/IPolygonModel";
import type { IPolylinePropertyBagModel } from "$lib/models/IPolylinePropertyBagModel";
import { floatingSidemenuState } from "$lib/stores/floating-sidemenu-store";
import { Color, ColorMaterialProperty, CustomDataSource, defined, JulianDate, PolygonHierarchy, type CzmlDataSource, type Entity } from "cesium";
import { get } from "svelte/store";
import { ENTITY_ID_PREFIX, SideMenuOptions } from "$lib/utils/constants";
import { trenchStateStore } from "$lib/stores/trench-store";
import type { ICableModel } from "$lib/models/ICableModel";
import { tick } from "svelte";
import { toast } from "$lib/components/organisms/toast/toast";
import { handleImageAreaEntitySelected, handleImageAreaEntityDeselected } from "$lib/core/map/maptools/imageAreaTool";

/**
 * Loadcase line id shapes — all resolve to the underlying `line:<numericId>`:
 *  - `<loadcaseName> line:<numericId>`
 *  - `LineTerrain:<loadcaseName> line:<numericId>` (new server format)
 *  - `LineTerrainLabel:<loadcaseName> line:<numericId>` (new server format)
 *  - `LineTerrain:<numericId>` (old server format)
 *  - `LineTerrainLabel:<numericId>` (old server format)
 *
 * We match by id shape rather than by owning-datasource name: when the
 * loadcase CZML is loaded, its `document` packet's `name` field overrides the
 * CzmlDataSource's constructor-time name, so the runtime owner name is just
 * the loadcase (e.g. `Ice`) and not the `area-tool-loadcase-lines:` prefix
 * we set in code. The id shapes are specific enough on their own — a regular
 * `line:<num>` has no leading space and doesn't match.
 */
const extractUnderlyingLineId = (entityId: string): string | null => {
    const newShape = entityId.match(/ line:(\d+)$/);
    if (newShape) return `line:${newShape[1]}`;
    const oldTerrain = entityId.match(/^LineTerrain(?:Label)?:(\d+)$/);
    if (oldTerrain) return `line:${oldTerrain[1]}`;
    return null;
};

let entity : Entity | null | undefined;

const removeDrawnEntity = () => {
    const viewer = useCesiumViewer();
    const entityToRemove = viewer.selectedEntity;
        const ownerDatasource = entityToRemove?.entityCollection.owner as CzmlDataSource | CustomDataSource | undefined;

        if (entityToRemove && entityToRemove.id.includes('distance_polyline_label')) {
            
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else {
                const { measureDistancePolylineDataSource } = useDataSources();

                const referenceLineId =
                    getEntityPropertyBag<IPolylinePropertyBagModel>(entityToRemove).lineId;
                const distanceLineEntity = measureDistancePolylineDataSource.entities.getById(
                    `distance_polyline:${referenceLineId}`
                );
                if(distanceLineEntity){
                    measureDistancePolylineDataSource.entities.removeById(distanceLineEntity.id);
                }
                
                measureDistancePolylineDataSource.entities.removeById(entityToRemove.id);
            }

        }

        if (entityToRemove && entityToRemove.id.includes('distance_polyline')) {
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else{
                const { measureDistancePolylineDataSource } = useDataSources();

                const referenceLineId =
                    getEntityPropertyBag<IPolylinePropertyBagModel>(entityToRemove).lineId;
                const distanceLineLabelEntity = measureDistancePolylineDataSource.entities.getById(
                    `distance_polyline_label:${referenceLineId}`
                );

                if (distanceLineLabelEntity) {
                    measureDistancePolylineDataSource.entities.removeById(distanceLineLabelEntity.id);
                }

                measureDistancePolylineDataSource.entities.removeById(entityToRemove.id);
            }
        }

        if (entityToRemove && entityToRemove.id.includes('sandbox_label')) {
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else{
                const { sandbox_LabelDataSource } = useDataSources();
                sandbox_LabelDataSource.entities.removeById(entityToRemove.id);
            }
        }

        if (entityToRemove && entityToRemove.id.includes('sandbox_marker')) {
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else{
                const { sandbox_MarkerDataSource } = useDataSources();
                sandbox_MarkerDataSource.entities.removeById(entityToRemove.id);
            }
        }

        if (entityToRemove && entityToRemove.id.includes('sandbox_rectangle')) {
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else{
            const { sandbox_RectangleDataSource } = useDataSources();
            sandbox_RectangleDataSource.entities.removeById(entityToRemove.id);
            }
        }

        if (entityToRemove && entityToRemove.id.includes('sandbox_box')) {
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else{
            const { sandbox_BoxDataSource } = useDataSources();
            sandbox_BoxDataSource.entities.removeById(entityToRemove.id);
            }
        }

        if (entityToRemove && entityToRemove.id.includes('sandbox_polygon')) {
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                removeSelectablePointsFromPolygonEdges({
                    referencePolygonId:
                        getEntityPropertyBag<IDrawnPolygonShape>(entityToRemove).referencePolygonId,
                        datasource: ownerDatasource
                });
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else{
                const { sandbox_polygonDataSource } = useDataSources();
                if (entityToRemove.entityCollection.owner == sandbox_polygonDataSource) {
                    removeSelectablePointsFromPolygonEdges({
                        referencePolygonId:
                            getEntityPropertyBag<IDrawnPolygonShape>(entityToRemove).referencePolygonId,
                            datasource: sandbox_polygonDataSource
                    });
                    sandbox_polygonDataSource.entities.removeById(entityToRemove.id);
                }
            }
        }

        if (entityToRemove && entityToRemove.id.includes('sandbox_polyline')) {
            removeCornerPointsFromPolyline({
                referenceLineId: getEntityPropertyBag<IPolylinePropertyBagModel>(entityToRemove).lineId
            });
            removeAllPointsFromPolyline({
                referenceLineId: entityToRemove.id
            });
            if(ownerDatasource?.name.startsWith('planDrawing:')){
                ownerDatasource.entities.removeById(entityToRemove.id);
            }else {
                const { sandbox_PolylineDataSource } = useDataSources();
                sandbox_PolylineDataSource.entities.removeById(entityToRemove.id);
            }
        }

		viewer.selectedEntity = undefined;
        return null;
}


const handleChangeEditDrawnEntity = (entity: Entity | undefined) => {
    if (
        entity?.id.startsWith('sandbox_polyline:') &&
        get(mapToolsStore).editDrawnEntity.entityId !== entity.id
    ) {
        removeAllDrawnPolylineDragHandlesForEditablePolyline();

        mapToolsStore.update((store) => {
            if (!entity) return store;

            store.editDrawnEntity.entityId = entity.id;
            return store;
        });

        toggleEditPolylineInMap();
    }

    entity = undefined;
    return null;
};

/**
 * A function which is for debugging purposes only. Logs details about the entity change.
 * @param previouslySelectedEntity 
 * @param entity 
 * @param toolEnabled 
 */
const runChangedDebug = (
        previouslySelectedEntity: Entity | null | undefined, 
        entity: Entity | null | undefined,
        toolEnabled: boolean) => {
    const debugObject = {
        previouslySelectedEntity: previouslySelectedEntity ? previouslySelectedEntity.id : previouslySelectedEntity,
        currentEntity: entity ? entity.id : entity,
        anyToolEnabled: toolEnabled
    }

    console.table(
        debugObject
    )
    console.log(entity);
}

const handleToolSpecificSelection = () => {
    const viewer = useCesiumViewer();
    // When the "edit drawn entity" tool is enabled, extract the entity ID of the clicked (selected) entity, but avoid actually selecting it (we only need the click event to find which entity the user wants to edit).
    if (get(mapToolsStore).editDrawnEntity.toolEnabled) {
        // The store should only update if the user clicks an actual drawn polyline, and the polyline is not the same as the previously clicked (and still actively edited) polyline.		
        handleChangeEditDrawnEntity(viewer.selectedEntity);
    } /*else if (get(mapToolsStore).moveExistingMastPoint.toolEnabled) {
        handleChangedEntityMoveMastpoint(viewer.selectedEntity)
    }*/ else if (
        get(mapToolsStore).drawRectangle.toolEnabled ||
        get(mapToolsStore).drawPolygon.toolEnabled ||
        get(mapToolsStore).drawPolyline.toolEnabled ||
        get(mapToolsStore).drawMeasurementLine.toolEnabled ||
        get(mapToolsStore).createTerrainProfileFromPoles.toolEnabled ||
        get(mapToolsStore).addMastPoint.toolEnabled ||
        get(mapToolsStore).addLabel.toolEnabled
    ) {
        viewer.selectedEntity = undefined;
    } else if (get(mapToolsStore).removeSandboxEntities.toolEnabled) {
        removeDrawnEntity();
    } else if (get(isAnyToolEnabled)) {
        // If a tool is enabled, do not return the entity
    }
    return null
}


/** supportPoleId:203
 * This function handles the UI and map behaviour when the selected entity changes and makes specific rules for what should happen as soon as the selected entity changes.
 * @param selectedEntity 
 * @returns 
 */
const entityChangeBehaviourCallback = async (selectedEntity: Entity | null | undefined) => {
    const ENABLE_DEBUG = false;

    const viewer = useCesiumViewer();
    const {sandbox_polygonDataSource} = useDataSources();
    const toolEnabled = get(isAnyToolEnabled);

    if (page.url.searchParams.has('mastpoint')) {
        // Remove the mastpoint query param from the /map URL
        const url = new URL(location.toString());
        url.searchParams.delete('mastpoint');
        replaceState(url.href, {
            project: {
                id: undefined,
                name: undefined
            },
            plan: {
                id: undefined,
                name: undefined
            }
        });
    }
    const previouslySelectedEntity = entity;
    entity = selectedEntity;

    if(ENABLE_DEBUG){
        runChangedDebug(previouslySelectedEntity, entity, toolEnabled);
    }
    
    // Unmark corridors and reset trench store
    if((previouslySelectedEntity?.id.includes('cablePath:') || previouslySelectedEntity?.id.includes('trenchPath:')) && defined(previouslySelectedEntity.corridor)){
        if(previouslySelectedEntity.id.includes('cablePath:')){
            if(!defined(previouslySelectedEntity.model)){
                // We don't want a corridor material if 3D model is rendered in map
                previouslySelectedEntity.corridor.material = new ColorMaterialProperty(Color.BEIGE.withAlpha(1));
            } else {
                previouslySelectedEntity.corridor.material = new ColorMaterialProperty(Color.TRANSPARENT);
            }
        } else {
            previouslySelectedEntity.corridor.material = new ColorMaterialProperty(Color.TRANSPARENT);
        }
    }

    //Removes a capacity report polygon if an entity of another type is selected.
    if (
        !selectedEntity?.id?.includes('sandbox_capacityreport_polygon:') &&
        previouslySelectedEntity?.id?.includes('sandbox_capacityreport_polygon:')
    ) {
        sandbox_polygonDataSource.entities.remove(previouslySelectedEntity);
    }

    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;

    if (defined(entity) && entity && !toolEnabled) {

        if (
            entity?.id?.includes('Loading...') &&
            viewer.selectedEntity?.description?.getValue()
        ) {
            // RETURN EARLY. THE ENTITY IS STILL LOADING DATA AND WILL TRIGGER A NEW CHANGE WHEN FINISHED.
            // HANDLE THIS IN THE NEXT ITERATION WITH PREVIOUSLYSELECTEDENTITY.
            return;
        }

        if (previouslySelectedEntity?.id?.includes('Loading...') &&
        viewer.selectedEntity?.description?.getValue()) {
            if(!entity){
                // We have clicked in the map outside the hitbox of a WMTS layer object.
                setSelectedEntity(undefined);
                floatingSidemenuState.set(SideMenuOptions.NONE);
            } if(entity && entity.description?.getValue()){
                // We have successfully clicked on and fetched a WMTS layer object.
                setSelectedEntity(entity);
                floatingSidemenuState.set(SideMenuOptions.SHOW_LAYERPICKINGINFO);
            }
            return;
        }
        
        // Quick-fix to "force"-remount the side menu to actually display the selected pole. Prior to this, stale data was displayed.
        // This should no longer be needed as the sidemenu has been restructured.
        //currentSideMenuOption = SideMenuOptions.NONE;
        floatingSidemenuState.set(SideMenuOptions.SHOW_INFOGRID);

        // Display the aviation obstacle side menu
        if (entity?.id?.includes('AviationObstacle:')) {
            floatingSidemenuState.set(SideMenuOptions.AVIATION_OBSTACLE_SELECTED);
        }
        // Image-area tool entities: an image billboard opens the sidebar with
        // that image (+ lightbox); the drawn polygon highlights and lists all.
        if (
            entity?.id?.startsWith(ENTITY_ID_PREFIX.SMARTDATA_IMAGE) ||
            entity?.id?.startsWith(ENTITY_ID_PREFIX.IMAGE_AREA)
        ) {
            handleImageAreaEntitySelected(entity);
        }
        // Selecting something else clears the image-area polygon's highlight.
        if (!entity?.id?.startsWith(ENTITY_ID_PREFIX.IMAGE_AREA)) {
            handleImageAreaEntityDeselected(previouslySelectedEntity);
        }
        if(entity?.id?.includes('trenchPath:') && defined(entity.corridor)){
            floatingSidemenuState.set(SideMenuOptions.NONE);
            return;
            floatingSidemenuState.set(SideMenuOptions.TRENCH_ENTITY_SELECTED);
            // Corridor which is clamped can't have outlines.
            //entity.corridor.outline = new ConstantProperty(true);
            //entity.corridor.outlineColor = new ColorMaterialProperty(Color.ORANGE);
            //entity.corridor.outlineWidth = new ConstantProperty(2);
        }
        if (entity?.id?.includes('cablePath:') && defined(entity.corridor)) {
            const entityPropertybag = getEntityPropertyBag<ICableModel.CablePathPropertyBagModel>(entity);
		    trenchStateStore.set(entityPropertybag.data);
            // Avoid rendering 2D grid before state is updated and make sure component mounts properly
            await tick();
            floatingSidemenuState.set(SideMenuOptions.CABLE_ENTITY_SELECTED);
            
            if(!defined(entity.model)){
                entity.corridor.material = new ColorMaterialProperty(Color.ORANGE.withAlpha(0.7));
            }
            // Corridor which is clamped can't have outlines.
            //entity.corridor.outline = new ConstantProperty(true);
            //entity.corridor.outlineColor = new ColorMaterialProperty(Color.ORANGE);
            //entity.corridor.outlineWidth = new ConstantProperty(2);
        }

        if (
            previouslySelectedEntity?.id?.includes('sandbox_polygon:') ||
            previouslySelectedEntity?.id?.includes('ProjectPolygon:') ||
            previouslySelectedEntity?.id?.includes('sandbox_capacityreport_polygon')
        ) {
            toggleHightlightPolygon({
                polygonEntity: previouslySelectedEntity,
                highlight: false
            });
        }

        if (
            previouslySelectedEntity &&
            (getEntityPropertyBag(previouslySelectedEntity)?.entityType ===
                EntityTypes.POLYGON_PART ||
                getEntityPropertyBag(previouslySelectedEntity)?.entityType ===
                    EntityTypes.PROJECT_POLYGON_PART)
        ) {
            toggleMovePolygonEdge({ screenSpaceEventHandler, move: false });
            toggleEntityVisuals([previouslySelectedEntity], false);
        }

        if (
            getEntityPropertyBag(entity)?.entityType === EntityTypes.DISTANCE_MEASURE_LINE_PART
        ) {
            floatingSidemenuState.set(SideMenuOptions.MEASUREMENT_TOOLBAR);
        }

        if (
            getEntityPropertyBag(entity)?.entityType === EntityTypes.POLYGON_PART ||
            getEntityPropertyBag(entity)?.entityType === EntityTypes.PROJECT_POLYGON_PART
        ) {
            toggleMovePolygonEdge({ screenSpaceEventHandler, move: true });
            toggleEntityVisuals([entity], true);
        }



        if (entity?.id?.includes(ENTITY_ID_PREFIX.MASTPOINT) || 
            entity.id.startsWith(ENTITY_ID_PREFIX.POLE) || 
            entity.id.startsWith(ENTITY_ID_PREFIX.UTILITYLINK) || 
            entity.id.startsWith(ENTITY_ID_PREFIX.CROSSBEAM) || 
            entity.id.startsWith(ENTITY_ID_PREFIX.SUPPORT) ||
            entity.id.startsWith(ENTITY_ID_PREFIX.VIRTUAL_SUPPORT)
        ) {
            if( previouslySelectedEntity?.parent && entityIsPartOfMastpoint(getEntityPropertyBag(entity))){
                // Logic for when a mastpoint is selected directly after another mastpoint (or part of mastpoint) was selected.
                floatingSidemenuState.set(SideMenuOptions.NONE);
                floatingSidemenuState.set(SideMenuOptions.SHOW_INFOGRID);
            }
            /*if (!toolEnabled) {
                if(entity.id.includes(ENTITY_ID_PREFIX.MASTPOINT)){
                    createProtractor(entity);
                } else {
                    const mastpointEntity = entity.parent as Entity;
                    createProtractor(mastpointEntity);
                }
            }*/
        } else if (entity?.id?.includes(ENTITY_ID_PREFIX.LINE)) {
            toggleEntityVisuals([entity], true);
        }
       
    } else {
        setSelectedEntity(undefined);
        selectedMapEntity.set(undefined);

        handleImageAreaEntityDeselected(previouslySelectedEntity);

        if (previouslySelectedEntity?.id?.startsWith('sandbox_polygon:')) {
            toggleHightlightPolygon({
                polygonEntity: previouslySelectedEntity,
                highlight: false
            });
        }
        if (previouslySelectedEntity?.id?.startsWith('sandbox_capacityreport_polygon:')) {
            sandbox_polygonDataSource.entities.remove(previouslySelectedEntity);
        }
        if (
            previouslySelectedEntity &&
            (getEntityPropertyBag(previouslySelectedEntity)?.entityType ===
                EntityTypes.POLYGON_PART ||
                getEntityPropertyBag(previouslySelectedEntity)?.entityType ===
                    EntityTypes.PROJECT_POLYGON_PART)
        ) {
            toggleMovePolygonEdge({ screenSpaceEventHandler, move: false });
            toggleEntityVisuals([previouslySelectedEntity], false);
        }

        if(previouslySelectedEntity && entityIsPartOfMastpoint(getEntityPropertyBag(previouslySelectedEntity)) ){
            floatingSidemenuState.set(SideMenuOptions.NONE);
        }

        // Automatically close the sidemenu if no tool is enabled, and if the previous entity was _not_ an entity from a WMS layer
        // Reason is that the sidemenu should stay open if the user deselects a WMS entity.
        if (
            !toolEnabled &&
            !previouslySelectedEntity?.description?.getValue(new JulianDate())
        ) {
            floatingSidemenuState.set(SideMenuOptions.NONE);
        }
    }
}

/**
 * This function handles specific rules tied to the selected entity. More specifically it handles what kind of entities should be selectable and 
 * how the selection indicator should behave.
 * @param selectionIndicatorElement 
 * @returns 
 */
const handleEntitySelection = async (selectionIndicatorElement: HTMLElement | null) => {
    const viewer = useCesiumViewer();
    if( viewer.selectedEntity?.id?.includes('Loading...') &&
        viewer.selectedEntity?.description?.getValue(new JulianDate())){
        //@ts-ignore TS-error in documentation. Check The description of viewer.selectedEntity.
        if(viewer.selectedEntity.feature){
            // This is a 3D tile feature;
            /* Enable to inspect and log feature properties
            const feature = viewer.selectedEntity.feature
            if (feature instanceof Cesium3DTileFeature) {
                const propertyIds = feature.getPropertyIds();
                const length = propertyIds.length;
                for (let i = 0; i < length; ++i) {
                    const propertyId = propertyIds[i];
                    console.log(`${propertyId}:`, feature.getProperty(propertyId));
                }
            }*/
            return;
        }
        // If the user selects an entity rendered by a WMS layer, the ID should initially be "Loading...", then resolve to something WMS-specific when the result has loaded.
        // Added a timeout, as the entity needs to get resolved in order to display metadata
        return viewer.selectedEntity;

    }
    if (
        defined(viewer.selectedEntity) &&
        viewer.selectedEntity &&
        viewer.selectedEntity.id.includes('AviationObstacle:')
    ) {
        selectedMapEntity.set(viewer.selectedEntity);
    }
    // Regular selection
    // Remember to add distance_polyline_label with callback null. labels should not be selected;
    if (
        defined(viewer.selectedEntity) &&
        viewer.selectedEntity &&
        getEntityPropertyBag(viewer.selectedEntity)
    ) {
        selectedMapEntity.set(viewer.selectedEntity);
        //@ts-ignore At this point an entity should have a data attribute
        const { data: entityData, entityType } = getEntityPropertyBag(viewer.selectedEntity);

        // Do not display the selection indicator for the following entities, and make them unable to be "selected"
        if (viewer.selectedEntity?.id?.startsWith('sandbox_polygon:') || 
        viewer.selectedEntity?.id?.startsWith('ProjectPolygon:') || 
        viewer.selectedEntity?.id?.startsWith('TerrainProfile:') || 
        viewer.selectedEntity?.id?.startsWith('LineTerrain') || 
        viewer.selectedEntity.id.startsWith('sandbox_polyline:') ||
        viewer.selectedEntity.id.startsWith('distance_polyline:') || 
        (entityType && entityType === EntityTypes.PROJECT_POLYGON)) {
            //Do not select a polygon drawn from drawingtools
            if (selectionIndicatorElement !== null) {
                selectionIndicatorElement.style.fill = 'transparent';
                selectionIndicatorElement.style.stroke = 'transparent';
                selectionIndicatorElement.style.visibility = 'hidden';
            }
            return null;
        }

        // Do not display the selection indicator for the following entities
        if (
            viewer.selectedEntity?.id?.startsWith('sandbox_capacityreport_polygon:') ||
            viewer.selectedEntity?.id?.startsWith('distance_polyline_label:') || 
            entityType && viewer.selectedEntity.id.startsWith('cablePath:')
        ) {
            if (selectionIndicatorElement !== null) {
                selectionIndicatorElement.style.fill = 'transparent';
                selectionIndicatorElement.style.stroke = 'transparent';
                selectionIndicatorElement.style.visibility = 'hidden';
            }
        }
       

        if (defined(entityData)) {
            if (selectionIndicatorElement !== null && entityType !== EntityTypes.LINE) {
                // Timeout needed to avoid flickering, as the previously selected entity will "flash" visible.
                setTimeout(() => {
                    (selectionIndicatorElement as HTMLElement).style.visibility = 'visible';
                }, 100);
            }
        } else {
            if (entityType === EntityTypes.POLYGON_PART) {
                const selectionIndicatorElement =
                    viewer?.selectionIndicator?.viewModel?.selectionIndicatorElement?.firstChild;
                if (selectionIndicatorElement !== null) {
                    //(selectionIndicatorElement as HTMLElement).style.visibility = 'hidden';
                }
            } else if (
                entityType === EntityTypes.POLYGON_SHAPE ||
                entityType === EntityTypes.PROJECT_POLYGON_SHAPE
            ) {
                const { mapdataDataSource } = useDataSources();
                
                let allMapEntities: Entity[];
                const liveEntities = mapdataDataSource.entities.values;
                const planDataSource = get(mapModeStore).activeDataSource;

                if(planDataSource && planDataSource.entities.values.length){
                    allMapEntities = liveEntities.concat(planDataSource.entities.values);
                } else{
                    allMapEntities = liveEntities;
                }

                const polygonBoundaries: PolygonHierarchy =
                    viewer?.selectedEntity?.polygon?.hierarchy?.getValue(new JulianDate());

                if (polygonBoundaries) {
                    const boundaries: [number, number][] = [];
                    for (let i = 0; i < polygonBoundaries.positions.length; i++) {
                        const { latitude, longitude } = cartesianToLatitudeLongitude(
                            polygonBoundaries.positions[i]
                        );
                        boundaries.push([longitude, latitude]);
                    }
                    const entitiesWithinBoundaries = getMapEntitiesWithinBoundaries(
                        allMapEntities,
                        boundaries
                    );

                    // Boundaries + area used to be stashed on the tools store here,
                    // but those fields moved to `area-mode-state.svelte.ts` (which
                    // is owned by the polygon-drawing flow, not the
                    // polygon-shape-selection flow this branch covers). The only
                    // consumer of that data here was the area-mode panel — keep
                    // `setSelectedMapEntities` because that *is* still wired up
                    // app-wide.
                    mapToolsStore.setSelectedMapEntities(entitiesWithinBoundaries);
                }
            } else {
                if (entityType !== EntityTypes.LINE) {
                    const selectionIndicatorElement =
                        viewer?.selectionIndicator?.viewModel?.selectionIndicatorElement?.firstChild;
                    //(selectionIndicatorElement as HTMLElement).style.fill = 'black';
                    // Timeout needed to avoid flickering, as the previously selected entity will "flash" visible.
                    setTimeout(() => {
                        (selectionIndicatorElement as HTMLElement).style.visibility = 'visible';
                    }, 100);
                }
                //alert("You have selected an entity not containing data properties. Editing these entities is currently work-in-progress.");
                //cb(undefined);
            }
        }

        return viewer.selectedEntity;

    } else {
        // Objektinformasjon fra WMS kommer her
        console.warn('No entity handling for: ' + viewer.selectedEntity?.id);
        selectedMapEntity.set(viewer.selectedEntity);
        if(viewer.selectedEntity?.description?.getValue()){
            return viewer.selectedEntity;
        }
        return undefined;
    }
}

/**
 * Loadcase line entities live in `area-tool-loadcase-lines:<name>` datasources
 * and are visual proxies for the underlying `line:<numericId>` entity. Without
 * intercepting, clicking one falls through the allowed-to-pick gate in
 * `map-event-listeners.ts` and shows the misleading "open a plan first" toast.
 *
 * If the click is a loadcase line, this redirects the selection to the real
 * line in the active plan (preferred) or the live datasource. The reassignment
 * re-fires `selectedEntityChanged`, so the normal selection flow handles the
 * resolved line on the next tick. If no underlying entity exists, the
 * selection is cleared and the user gets a targeted toast instead.
 *
 * Returns `true` when the click was a loadcase line and was handled here, so
 * the caller can bail out of the rest of the click pipeline.
 */
const redirectLoadCaseLineSelectionIfNeeded = (): boolean => {
    const viewer = useCesiumViewer();
    const entity = viewer.selectedEntity;
    if (!entity) return false;

    const underlyingId = extractUnderlyingLineId(entity.id);
    if (!underlyingId) return false;

    const { mapdataDataSource } = useDataSources();
    const activePlanDataSource = get(mapModeStore).activeDataSource;
    const target =
        activePlanDataSource?.entities.getById(underlyingId) ??
        mapdataDataSource.entities.getById(underlyingId);
    if (target) {
        ensureTargetVisibleForSelection(target);
        viewer.selectedEntity = target;
        return true;
    }

    toast({
        type: 'info',
        title: 'Ugyldig valg',
        description: 'Fant ikke den tilhørende ledningen på kartet.'
    });
    viewer.selectedEntity = undefined;
    return true;
};

/**
 * Loadcase rendering hides the underlying `line:<id>` entity while its visual
 * proxy is on screen. Cesium's selection pipeline (and downstream consumers
 * like `getEntityPropertyBag`) doesn't cope with `show = false` as the
 * selected entity, so we flip the target visible for the duration of the
 * selection and restore the previous value as soon as selection moves away.
 *
 * One slot is enough — Cesium only ever holds one `selectedEntity`. Switching
 * targets while an override is live restores the previous one first.
 */
let activeShowOverride: { entity: Entity; previousShow: boolean; dispose: () => void } | null = null;

const ensureTargetVisibleForSelection = (target: Entity): void => {
    if (activeShowOverride && activeShowOverride.entity !== target) {
        activeShowOverride.entity.show = activeShowOverride.previousShow;
        activeShowOverride.dispose();
        activeShowOverride = null;
    }
    if (activeShowOverride?.entity === target) return;
    if (target.show === true) return;

    const previousShow = target.show;
    target.show = true;

    const viewer = useCesiumViewer();
    const dispose = viewer.selectedEntityChanged.addEventListener(() => {
        if (viewer.selectedEntity === target) return;
        target.show = previousShow;
        dispose();
        activeShowOverride = null;
    });
    activeShowOverride = { entity: target, previousShow, dispose };
};

export {entityChangeBehaviourCallback, handleToolSpecificSelection, removeDrawnEntity, handleEntitySelection, redirectLoadCaseLineSelectionIfNeeded};